<template>
  <v-container
    class="pricing-page"
    :class="embedded ? 'pa-0' : 'py-10'"
  >
    <div
      v-if="!embedded"
      class="text-left mb-8"
    >
      <h1 class="text-h3 mb-3">
        Plans &amp; Subscriptions
      </h1>
      <p class="text-body-1">
        Start free. Choose more storage and AI credits when you need them.
      </p>
      <v-btn
        v-if="$store.state.logged"
        text
        color="primary"
        to="/billing"
      >
        My subscription
      </v-btn>
    </div>
    <v-btn
      v-if="embedded && $store.state.logged"
      text
      color="primary"
      to="/billing"
      class="mb-4"
    >
      My subscription
    </v-btn>
    <v-alert
      v-if="error"
      type="info"
      outlined
    >
      {{ error }}
      <v-btn
        text
        small
        :loading="loading"
        @click="loadCatalog"
      >
        Try again
      </v-btn>
    </v-alert>
    <v-alert
      v-if="catalog && catalog.checkoutEnabled && catalog.mode === 'test'"
      type="info"
      outlined
    >
      Stripe test mode is on. Use a test card; no real money is charged.
    </v-alert>
    <v-progress-linear
      v-if="loading"
      indeterminate
      aria-label="Loading plans"
    ></v-progress-linear>
    <v-row
      v-if="catalog"
      :class="{ 'mx-0': embedded }"
    >
      <v-col
        v-for="plan in catalog.plans"
        :key="plan.id"
        cols="12"
        sm="6"
        :lg="embedded ? 6 : 3"
      >
        <v-card
          outlined
          height="100%"
          class="pa-6 d-flex flex-column"
          :class="{ 'featured-plan': plan.id === 'personal' }"
        >
          <h2 class="text-h5 mb-4">
            {{ plan.name }}
          </h2>
          <p><strong class="text-h3">{{ money(plan.amount) }}</strong><span v-if="plan.amount"> / month</span></p>
          <p
            v-if="descriptions[plan.id]"
            class="plan-description"
          >
            {{ descriptions[plan.id] }}
          </p>
          <ul class="plan-benefits mb-6">
            <li>{{ storage(plan.storageBytes) }} of storage</li>
            <li v-if="plan.creditPeriod === 'welcome'">
              {{ plan.credits }} one-time welcome AI credits, valid for 30 days
            </li>
            <li v-else>
              {{ plan.credits }} AI credits each billing cycle
            </li>
            <li>Personal and Professional biographies</li>
            <li>Photos, videos, and diary</li>
            <li>Keep control of your privacy</li>
          </ul>
          <v-spacer></v-spacer>
          <v-btn
            :color="plan.id === 'personal' ? 'primary' : undefined"
            :outlined="plan.id !== 'personal'"
            :disabled="plan.amount > 0 && !catalog.checkoutEnabled"
            :to="destination(plan)"
            block
          >
            {{ plan.amount === 0 ? 'Start free' : catalog.checkoutEnabled ? `Choose ${plan.name}` : 'Coming soon' }}
          </v-btn>
        </v-card>
      </v-col>
    </v-row>
    <v-card
      v-if="catalog"
      outlined
      class="pa-6 mt-6"
    >
      <h2 class="text-h5 mb-3">
        Need more AI credits?
      </h2>
      <p>{{ catalog.aiPack.credits }} additional credits for {{ money(catalog.aiPack.amount) }}. One-time purchase. No expiration.</p>
      <v-btn
        color="primary"
        outlined
        :disabled="!catalog.checkoutEnabled"
        :to="packDestination"
      >
        View AI pack
      </v-btn>
    </v-card>
    <div class="mt-8 text-body-2">
      <p>Existing subscriptions keep their current prices and allowances until you choose to change plans.</p>
      <p>Monthly credits do not roll over. Purchased credits remain separate from your subscription.</p>
      <p>Need more storage? Choose a larger plan. There are no separate storage packs.</p>
      <p>Cancel at the end of your billing period. Your photos and stories are not automatically deleted when you cancel.</p>
    </div>
  </v-container>
</template>

<script>
import billing from '@/services/billing'

export default {
  name: 'BillingPlans',
  props: { embedded: { type: Boolean, default: false } },
  data: () => ({
    catalog: null,
    loading: false,
    error: '',
    descriptions: {
      free: 'Discover Self Biography. Create your account, explore the platform, and begin recording your memories with introductory storage and AI credits.',
      personal: 'Preserve your life. Organize memories, photographs, stories, family history, travels, and everyday experiences. Your subscription also supports the continued development of Self Biography.',
      professional: 'Build your life’s work. Preserve career milestones, projects, portfolios and accomplishments, with five times the storage and AI credits of Personal.',
      business_plus: 'Grow a substantial archive. Preserve business milestones, creative work and large collections with three times Professional’s storage and twice its AI credits.',
    },
  }),
  computed: {
    packDestination() {
      return this.$store.state.logged ? '/billing?offer=ai100' : '/auth'
    },
  },
  mounted() { this.loadCatalog() },
  methods: {
    money(amount) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(amount / 100) },
    storage(bytes) { return bytes < 1000000000 ? `${bytes / 1000000} MB` : `${bytes / 1000000000} GB` },
    destination(plan) {
      if (!this.$store.state.logged) return '/auth'

      return plan.id === 'free' ? '/my-pictures' : `/billing?offer=${plan.id}`
    },
    async loadCatalog() {
      this.loading = true
      this.error = ''
      try { this.catalog = await billing.catalog() } catch (_) {
        this.error = 'Plans are being prepared. Your existing account remains available.'
      } finally { this.loading = false }
    },
  },
}
</script>

<style scoped>
.pricing-page { max-width: 1120px; }
.plan-benefits { line-height: 2; padding-left: 20px; }
.featured-plan { border: 2px solid var(--v-primary-base) !important; }
</style>
